import apisauce from 'apisauce'
import moment from 'moment'
import {
  AppointmentsApiFactory,
  AvailabilitiesApiFactory,
  BookingsApiFactory,
  ProvidersApiFactory,
  Configuration,
  UpsertAppointmentResponseV1,
  UpsertAppointmentRequestV1,
  AppointmentV2,
} from '@dialogue/timekeeper'
import { select, call } from 'typed-redux-saga'

import Config from 'APP/Config'
import { RootState } from 'APP/Store/CreateStore'
import { AxiosInstance } from 'axios'
import {
  CreateAppointmentPayload,
  UpsertAppointmentForBookingRequest,
} from 'APP/Store/AppointmentBooking/types'
import { snakeCaseKeys } from 'APP/Lib/SnakeCaseHelper'
import { logger } from 'APP/Lib/Datadog'
import { DATE_FORMAT } from 'APP/Redux/AppointmentBookingRedux'
import { AvailabilitiesExists } from 'APP/Components/AppointmentBooking/utils/types'

export const selectAccessToken = (state: RootState) => state.login.accessToken

export function* getClientConfig(): Generator<any, Configuration, any> {
  const accessToken = yield* select(selectAccessToken)

  if (!accessToken) {
    throw new Error('Missing access token')
  }

  if (!Config.TIMEKEEPER_DOMAIN) {
    throw new Error('Timekeeper domain is not set')
  }

  return new Configuration({
    accessToken,
    basePath: Config.TIMEKEEPER_DOMAIN,
  })
}

export function* getAxiosClient(): Generator<any, AxiosInstance, any> {
  const accessToken = yield* select(selectAccessToken)

  if (!accessToken) {
    throw new Error('Missing access token')
  }

  if (!Config.TIMEKEEPER_DOMAIN) {
    throw new Error('Timekeeper domain is not set')
  }

  const apiClient = apisauce.create({
    baseURL: Config.TIMEKEEPER_DOMAIN,
    headers: {
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  // AxiosInstance from apisauce is not the same version as the AxiosInstance from axios, so we need to cast it
  return apiClient.axiosInstance as AxiosInstance
}

function* createApi<T>(
  factory: (config: any, basePath: string, axios: any) => T
): Generator<any, T, any> {
  const axiosInstance = yield* call(getAxiosClient)
  const config = yield* call(getClientConfig)
  return factory(config, '', axiosInstance)
}

export function* getAppointmentsApi(): Generator<any, AppointmentsApi, any> {
  return yield* createApi(AppointmentsApiFactory)
}

export function* getAvailabilitiesApi(): Generator<any, AvailabilitiesApi, any> {
  return yield* createApi(AvailabilitiesApiFactory)
}

export function* getProvidersApi(): Generator<any, ProvidersApi, any> {
  return yield* createApi(ProvidersApiFactory)
}

type AvailabilitiesApi = ReturnType<typeof AvailabilitiesApiFactory>
type BookingsApi = ReturnType<typeof BookingsApiFactory>
type ProvidersApi = ReturnType<typeof ProvidersApiFactory>
type AppointmentsApi = ReturnType<typeof AppointmentsApiFactory>

export function* getBookingsApi(): Generator<any, BookingsApi, any> {
  return yield* createApi(BookingsApiFactory)
}

export function* upsertAppointmentForBooking(
  params: UpsertAppointmentForBookingRequest
): Generator<any, UpsertAppointmentResponseV1, any> {
  const api = yield* call(getBookingsApi)

  const { bookingId, ...appointment } = params

  const response = yield* call(
    api.upsertAppointment,
    bookingId,
    snakeCaseKeys(appointment as Parameters<typeof snakeCaseKeys>[0]) as UpsertAppointmentRequestV1
  )

  if (!response.data?.data) {
    throw new Error('Appointment creation failed: Missing appointment in response')
  }

  return response.data.data
}

export function* createAppointment(
  payload: CreateAppointmentPayload
): Generator<any, AppointmentV2, any> {
  const { bookingId } = payload
  if (!bookingId) {
    // Only the time-based saga logs its own failures, so log here to keep the
    // provider-based flow visible in Datadog too.
    logger.error('Appointment creation failed: Missing bookingId', {
      provider_id: payload?.provider_id,
    })

    throw new Error('Appointment creation failed: Missing bookingId')
  }

  const { appointment } = yield* call(upsertAppointmentForBooking, {
    bookingId,
    startAt: payload.start_at,
    endAt: payload.end_at,
    providerId: payload.provider_id,
  })

  return appointment
}

export interface FetchAvailabilitiesExistsParams {
  bookingId: string
}

export function* fetchAvailabilitiesExists(
  params: FetchAvailabilitiesExistsParams
): Generator<any, AvailabilitiesExists, any> {
  const { bookingId } = params

  const api = yield* call(getAvailabilitiesApi)
  const response = yield* call(api.checkAvailabilityExists, { booking_id: bookingId })
  const perDay = response.data?.data ?? {}

  // Re-key from the API's ISO day format to DATE_FORMAT, which is how consumers
  // look this map up (e.g. `availabilitiesExists[day.format(DATE_FORMAT)]`).
  const normalized = Object.entries(perDay).reduce<AvailabilitiesExists>(
    (map, [rawDay, exists]) => {
      map[moment(rawDay).format(DATE_FORMAT)] = Boolean(exists)
      return map
    },
    {}
  )

  return normalized
}
